import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Libs/firebase-config.mjs';
import TestimonialCard from '../shared/TestimonialCard';

const TestimonialsSection = ({ darkMode }) => {
  const [testimonialsData, setTestimonialsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTestimonialsData();
  }, []);

  const loadTestimonialsData = async () => {
    try {
      const docRef = doc(db, 'testimonialsSection', 'content');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setTestimonialsData(docSnap.data());
      } else {
        // Fallback to default data
        setTestimonialsData({
          chapter: 'Chapter 08',
          title: 'Lives Transformed',
          testimonials: [
            {
              text: "Pastor Peter led me to Christ in 2005 in Eastlands, Nairobi. His teaching on Sally transformed my understanding of God's love. Nineteen years later, I'm now pastoring my own congregation in Thika.",
              name: "Rev. Peter Ochieng",
              role: "Lead Pastor, Sally Community Thika"
            },
            {
              text: "Pastor Sally's women's ministry changed my life completely. Through her mentorship, I found my voice and discovered God's purpose for me. She doesn't just preach about empowerment—she lives it.",
              name: "Pastor Faith Wanjiru",
              role: "Women's Ministry Leader, Nakuru"
            },
            {
              text: "Under Pastor Peter's mentorship at the Nairobi Leadership Institute, I discovered my calling to missions. His wisdom prepared me for ministry across East Africa. He doesn't just teach leadership—he models it.",
              name: "Bishop David Kimani",
              role: "Missionary, Coast Region"
            }
          ]
        });
      }
    } catch (err) {
      console.error('Error loading testimonials section:', err);
      setError('Failed to load content');
      // Set fallback data on error
      setTestimonialsData({
        chapter: 'Chapter 08',
        title: 'Lives Transformed',
        testimonials: [
          {
            text: "Pastor Peter led me to Christ in 2005 in Eastlands, Nairobi. His teaching on Sally transformed my understanding of God's love. Nineteen years later, I'm now pastoring my own congregation in Thika.",
            name: "Rev. Peter Ochieng",
            role: "Lead Pastor, Sally Community Thika"
          },
          {
            text: "Pastor Sally's women's ministry changed my life completely. Through her mentorship, I found my voice and discovered God's purpose for me. She doesn't just preach about empowerment—she lives it.",
            name: "Pastor Faith Wanjiru",
            role: "Women's Ministry Leader, Nakuru"
          },
          {
            text: "Under Pastor Peter's mentorship at the Nairobi Leadership Institute, I discovered my calling to missions. His wisdom prepared me for ministry across East Africa. He doesn't just teach leadership—he models it.",
            name: "Bishop David Kimani",
            role: "Missionary, Coast Region"
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <section 
        className={`py-32 px-8 ${darkMode ? 'bg-slate-800' : 'bg-white'} transition-colors duration-300`}
        style={{ minHeight: '400px', visibility: 'visible', opacity: 1 }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <div className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              Loading Testimonials...
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Don't render if no data
  if (!testimonialsData) {
    return null;
  }

  const data = testimonialsData;

  return (
    <section 
      className={`py-32 px-8 ${darkMode ? 'bg-slate-800' : 'bg-white'} transition-colors duration-300`}
      style={{ 
        minHeight: '400px',
        visibility: 'visible',
        opacity: 1,
        display: 'block',
        zIndex: 1
      }}
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className={`text-sm uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-600'} font-medium mb-4`}>
            {data.chapter}
          </div>
          <h2 className={`text-5xl md:text-6xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            {data.title}
          </h2>
        </motion.div>

        <div className="space-y-12">
          {data.testimonials && data.testimonials.length > 0 ? (
            data.testimonials.map((testimonial, index) => (
              <TestimonialCard 
                key={index} 
                testimonial={testimonial} 
                index={index} 
                darkMode={darkMode} 
              />
            ))
          ) : (
            <div className={`text-center text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              No testimonials available
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;